import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactFlow, Background, Controls, Handle, Position, BaseEdge, EdgeLabelRenderer, getSmoothStepPath, useReactFlow, useStore, useNodesInitialized } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FaBolt, FaDatabase, FaGlobeAmericas, FaLayerGroup, FaReply, FaPlus, FaCheck, FaExclamation, FaCodeBranch, FaTable, FaThumbtack, FaCode } from 'react-icons/fa';
import { NODE_TYPES, SUPABASE_OPS, SHEETS_OPS, COND_OPS, canAddRespond, depthOf } from '../../../utils/toolDefinition';
import { summarize } from './DataView';
import './toolCanvas.css';

// Lienzo de la herramienta, al estilo de n8n. Arriba «Cuando el agente la usa»; debajo, los pasos. Un «Si / no» abre dos caminos
// (Sí a la izquierda, No a la derecha) que se vuelven a juntar en un punto; una respuesta al agente termina su camino.
// Cada «+» agrega un paso justo ahí. Al probar, cada paso muestra si funcionó y el camino que se tomó.
const ICONS = { trigger: FaBolt, supabase: FaDatabase, http: FaGlobeAmericas, set: FaLayerGroup, respond: FaReply, if: FaCodeBranch, sheets: FaTable, code: FaCode };
const ACCENT = { trigger: '#FF6B3D', ...Object.fromEntries(Object.entries(NODE_TYPES).map(([k, v]) => [k, v.accent])) };
const GAP = 140;
const UNIT = 340;

function subtitle(n, draft) {
  if (n.type === 'trigger') { const ai = (draft.inputs || []).filter((i) => i.source === 'ai').length; return `${(draft.inputs || []).length} datos de entrada · ${ai} los saca la IA`; }
  if (n.type === 'supabase') return n.operation === 'rpc' ? `Función ${n.fn || '…'}` : `${SUPABASE_OPS.find((o) => o.key === n.operation)?.label || ''} · ${n.table || 'sin tabla'}`;
  if (n.type === 'sheets') return `${SHEETS_OPS.find((o) => o.key === n.operation)?.label || ''} · ${n.sheet || 'sin pestaña'}`;
  if (n.type === 'http') return `${n.method} ${(n.url ?? n.path ?? '/').replace(/^https:\/\//, '').replace(/\{\{\s*|\s*\}\}/g, '') || '…'}`;
  if (n.type === 'code') return `${(n.vars || []).length} variables · ${((n.code || '').match(/\n/g) || []).length + 1} líneas`;
  if (n.type === 'set') return `${(n.fields || []).length} campos`;
  if (n.type === 'if') {
    const c = (n.conditions || [])[0];
    const more = (n.conditions || []).length > 1 ? ` ${n.match === 'any' ? 'o' : 'y'} ${(n.conditions.length - 1)} más` : '';
    return c ? `${(c.left || '…').replace(/\{\{\s*|\s*\}\}/g, '')} ${COND_OPS.find((o) => o.key === c.op)?.label || ''} ${['empty', 'not_empty'].includes(c.op) ? '' : c.right || '…'}${more}` : 'Sin condición';
  }
  return (n.text || '').slice(0, 48) || 'Sin texto';
}

function StepNode({ data, selected }) {
  const Icon = ICONS[data.type] || FaLayerGroup;
  const r = data.result;
  return (
    <div className={`tc-node ${selected ? 'is-selected' : ''} ${r ? (r.ok ? 'is-ok' : 'is-error') : ''} ${data.dim ? 'is-dim' : ''}`} style={{ '--accent': ACCENT[data.type] }}>
      {data.type !== 'trigger' && <Handle type="target" position={Position.Top} isConnectable={false} />}
      <span className="tc-icon" aria-hidden><Icon /></span>
      <span className="tc-text">
        <span className="tc-title">{data.title}</span>
        <span className="tc-sub">{data.sub}</span>
        {r && <span className={`tc-out ${r.ok ? '' : 'err'}`}>{r.ok ? `${data.summary ? `${data.summary} · ` : ''}${r.ms < 1000 ? `${r.ms} ms` : `${(r.ms / 1000).toFixed(1)} s`}` : 'falló · ábrelo para ver el error'}</span>}
      </span>
      {r && <span className={`tc-badge ${r.ok ? 'ok' : 'err'}`} title={r.ok ? `Funcionó · ${r.ms} ms` : r.error}>{r.ok ? <FaCheck aria-hidden /> : <FaExclamation aria-hidden />}</span>}
      {!r && data.warnings > 0 && <span className="tc-warn" title={`${data.warnings} cosa(s) por completar`}>{data.warnings}</span>}
      {data.pinned && <span className="tc-pin" title="Datos fijados: al probar, este paso no se ejecuta" aria-label="Datos fijados"><FaThumbtack aria-hidden /></span>}
      <Handle type="source" position={Position.Bottom} isConnectable={false} />
    </div>
  );
}

// Un camino vacío: un hueco con «+» para poner el primer paso de esa rama.
function EmptyBranch({ data }) {
  return (
    <div className="tc-empty nodrag">
      <Handle type="target" position={Position.Top} isConnectable={false} />
      <AddMenu onPick={(t) => data.onInsert(data.addr, 0, t)} label={`Agregar un paso en «${data.label}»`} allowRespond allowIf={data.allowIf} />
      <span>Camino «{data.label}» vacío</span>
      <Handle type="source" position={Position.Bottom} isConnectable={false} />
    </div>
  );
}
// Donde se juntan los dos caminos de un «Si / no».
function Junction() {
  return (<div className="tc-junction"><Handle type="target" position={Position.Top} isConnectable={false} /><Handle type="source" position={Position.Bottom} isConnectable={false} /></div>);
}

function AddEdge({ id, sourceX, sourceY, targetX, targetY, data }) {
  const [path, lx, ly] = getSmoothStepPath({ sourceX, sourceY, targetX, targetY, borderRadius: 14 });
  return (
    <>
      <BaseEdge id={id} path={path} className={`tc-edge ${data?.taken === false ? 'is-skipped' : ''} ${data?.taken ? 'is-taken' : ''}`} />
      {data?.count && !data?.label && (
        <EdgeLabelRenderer>
          <div className="tc-count nodrag nopan" style={{ transform: `translate(-50%, -50%) translate(${sourceX}px, ${sourceY + 16}px)` }}>{data.count}</div>
        </EdgeLabelRenderer>
      )}
      {(data?.label || data?.insert) && (
        <EdgeLabelRenderer>
          <div className="tc-add nodrag nopan" style={{ transform: `translate(-50%, -50%) translate(${data.label ? (sourceX + targetX) / 2 : lx}px, ${data.label ? sourceY + 34 : ly}px)` }}>
            {data.label && <span className={`tc-branch ${data.label === 'Sí' ? 'yes' : 'no'}`}>{data.label}</span>}
            {data.insert && <AddMenu onPick={(t) => data.onInsert(data.insert.addr, data.insert.index, t)} label="Agregar un paso aquí" allowRespond={data.insert.respond} allowIf={data.insert.allowIf} />}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export function AddMenu({ onPick, label, big = false, allowRespond = true, allowIf = true }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const types = Object.entries(NODE_TYPES).filter(([k]) => (k !== 'respond' || allowRespond) && (k !== 'if' || allowIf));
  return (
    <div ref={ref} className="relative">
      <button type="button" className={`tc-plus ${big ? 'is-big' : ''}`} onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={label} title={label}><FaPlus aria-hidden /></button>
      {open && (
        <div role="menu" className="tc-menu">
          {types.map(([k, v]) => {
            const Icon = ICONS[k];
            return (
              <button key={k} type="button" role="menuitem" onClick={() => { setOpen(false); onPick(k); }}>
                <span className="tc-icon sm" style={{ '--accent': v.accent }} aria-hidden><Icon /></span>
                <span><span className="block font-semibold">{v.label}</span><span className="block text-[11.5px] opacity-70">{v.hint}</span></span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const nodeTypes = { step: StepNode, empty: EmptyBranch, junction: Junction };
const edgeTypes = { add: AddEdge };

/**
 * Acomoda una lista de pasos centrada en `x` desde `y`. Devuelve sus nodos y conexiones, su ancho, hasta dónde llega (`bottom`),
 * su primer elemento y sus «salidas» (de dónde sigue el camino; una respuesta al agente no tiene salida).
 */
function layout(list, addr, x, y, ctx) {
  const out = { nodes: [], edges: [], width: UNIT, bottom: y, entry: null, exits: [] };
  let cy = y; let prev = null; // salidas pendientes: [{ id, insert }]
  const link = (target, label) => {
    for (const p of prev || []) out.edges.push(ctx.edge(p.id, target, { insert: p.insert, label: p.label }));
  };
  list.forEach((n, i) => {
    const ins = { addr, index: i + 1, respond: canAddRespond(ctx.nodes, addr), allowIf: depthOf(addr) < 3 };
    if (n.type !== 'if') {
      out.nodes.push(ctx.node(n, x, cy));
      if (prev) link(n.id); else out.entry = n.id;
      prev = n.type === 'respond' ? [] : [{ id: n.id, insert: ins }];
      cy += GAP;
      return;
    }
    out.nodes.push(ctx.node(n, x, cy));
    if (prev) link(n.id); else out.entry = n.id;
    const yy = cy + GAP + 10;
    const branch = (br, label) => {
      const a = [...addr, n.id, br];
      const lst = n[br] || [];
      if (!lst.length) {
        const pid = `__vacio_${n.id}_${br}`;
        return { width: UNIT, bottom: yy + GAP, nodes: [{ id: pid, type: 'empty', position: { x: 0, y: yy }, data: { addr: a, label, onInsert: ctx.onInsert, allowIf: depthOf(a) < 3 }, selectable: false }],
          edges: [], entry: pid, exits: [{ id: pid, insert: null }], addr: a, label };
      }
      return { ...layout(lst, a, 0, yy, ctx), addr: a, label };
    };
    const t = branch('then', 'Sí'); const e = branch('else', 'No');
    const total = t.width + e.width;
    const shift = (b, cx) => { for (const nd of b.nodes) nd.position = { ...nd.position, x: nd.position.x + cx }; };
    shift(t, x - total / 2 + t.width / 2); shift(e, x + total / 2 - e.width / 2);
    out.nodes.push(...t.nodes, ...e.nodes); out.edges.push(...t.edges, ...e.edges);
    for (const b of [t, e]) {
      const taken = ctx.results ? Boolean(ctx.taken[`${n.id}:${b.addr[b.addr.length - 1]}`]) : undefined;
      out.edges.push(ctx.edge(n.id, b.entry, { label: b.label, insert: b.nodes[0]?.type === 'empty' ? null : { addr: b.addr, index: 0, respond: canAddRespond(ctx.nodes, b.addr), allowIf: depthOf(b.addr) < 3 }, taken }));
    }
    out.width = Math.max(out.width, total);
    const exits = [...t.exits, ...e.exits];
    cy = Math.max(t.bottom, e.bottom) + 10;
    if (exits.length) {
      const jid = `__junta_${n.id}`;
      out.nodes.push({ id: jid, type: 'junction', position: { x: x + 141, y: cy }, data: {}, selectable: false });
      for (const ex of exits) out.edges.push(ctx.edge(ex.id, jid, { insert: ex.insert }));
      prev = [{ id: jid, insert: ins }];
      cy += 50;
    } else prev = [];
  });
  out.bottom = cy;
  out.exits = prev || [];
  return out;
}

/** Reencuadra el diagrama cuando el lienzo cambia de tamaño o termina de medir los pasos. Dentro de una capa (el editor abierto desde un agente)
 *  el lienzo nace con tamaño 0 y el encuadre inicial de fitView lo deja vacío; así se corrige solo. */
function Refit({ count }) {
  const { fitView } = useReactFlow();
  const dom = useStore((s) => s.domNode);
  const ready = useNodesInitialized();
  const fit = useRef(null);
  const run = useCallback(() => { clearTimeout(fit.current); fit.current = setTimeout(() => fitView({ padding: 0.25, maxZoom: 1, duration: 0 }), 60); }, [fitView]);
  useEffect(() => { if (ready) run(); }, [ready, count, run]);
  useEffect(() => {
    if (!dom || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(run); ro.observe(dom);
    return () => { ro.disconnect(); clearTimeout(fit.current); };
  }, [dom, run]);
  return null;
}

export default function ToolCanvas({ draft, selected, onSelect, onInsert, results, warnings, pinned }) {
  const { nodes, edges } = useMemo(() => {
    const taken = {};
    // ramas tomadas en la última prueba: un paso con branch 'then'/'else' cuenta para su «Si / no»
    const parentOf = {};
    const index = (list) => list.forEach((n) => { if (n.type === 'if') { (n.then || []).forEach((c) => { parentOf[c.id] = n.id; }); (n.else || []).forEach((c) => { parentOf[c.id] = n.id; }); index(n.then || []); index(n.else || []); } });
    index(draft.nodes || []);
    for (const r of Object.values(results || {})) if (r.branch && parentOf[r.id]) taken[`${parentOf[r.id]}:${r.branch}`] = true;
    for (const r of Object.values(results || {})) if (r.type === 'if' && r.result) taken[`${r.id}:${/^Sí/.test(r.result) ? 'then' : 'else'}`] = true;

    let eid = 0;
    const ctx = {
      nodes: draft.nodes || [], onInsert, results, taken,
      node: (n, x, y) => ({ id: n.id, type: 'step', position: { x, y }, draggable: false, selected: selected === n.id,
        data: { type: n.type, title: n.name || NODE_TYPES[n.type]?.label || n.id, sub: subtitle(n, draft), result: results?.[n.id],
          summary: n.type === 'respond' || n.type === 'if' ? null : summarize(results?.[n.id]?.output),
          pinned: Boolean(pinned && Object.prototype.hasOwnProperty.call(pinned, n.id)), dim: Boolean(results) && !results[n.id] && n.type !== 'trigger',
          warnings: (warnings?.[n.id === '__entrada' ? '_tool' : n.id] || []).length } }),
      edge: (source, target, data) => {
        const r = results?.[source];
        const count = r && r.ok && r.type !== 'if' && r.type !== 'respond' ? summarize(r.output) : null;
        return { id: `e${++eid}-${source}-${target}`, source, target, type: 'add', data: { ...data, onInsert, count } };
      },
    };
    const trigger = ctx.node({ id: '__entrada', type: 'trigger', name: 'Cuando el agente la usa' }, 0, 0);
    trigger.data.dim = false;
    const body = layout(draft.nodes || [], [], 0, GAP, ctx);
    const es = [...body.edges];
    if (body.entry) es.push(ctx.edge('__entrada', body.entry, { insert: { addr: [], index: 0, respond: canAddRespond(ctx.nodes, []), allowIf: true } }));
    return { nodes: [trigger, ...body.nodes], edges: es };
  }, [draft, selected, results, warnings, onInsert, pinned]);

  return (
    <div className="tc-wrap" aria-label="Pasos de la herramienta">
      <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes}
        onNodeClick={(_, n) => { if (n.type === 'step') onSelect(n.id); }}
        fitView fitViewOptions={{ padding: 0.25, maxZoom: 1 }} minZoom={0.3} maxZoom={1.5}
        nodesConnectable={false} nodesDraggable={false} elementsSelectable proOptions={{ hideAttribution: true }}>
        <Background gap={20} size={1.2} className="tc-bg" />
        <Controls showInteractive={false} position="bottom-left" />
        <Refit count={nodes.length} />
      </ReactFlow>
      <div className="tc-end">
        <AddMenu big onPick={(t) => onInsert([], (draft.nodes || []).length, t)} label="Agregar un paso al final" allowRespond={canAddRespond(draft.nodes || [], [])} />
      </div>
    </div>
  );
}
